/**
 * Delt statuspatch-hjelper for tilbud (VIB-1304). Brukes av BÅDE
 * tilbudseditoren (OfferEditPage/OfferEditHeader) og kanban-boardet
 * (OfferPipelineBoard) via src/hooks/use-offer-status-change.ts, slik at
 * sent_at/accepted_at/lost_at/valid_until settes IDENTISK fra begge flater
 * (jf. spec "Statusflyt" — samme kodesti fra begge flater).
 *
 * Ren funksjon — ingen Supabase/React-imports. Feature-flag-gating
 * (feature_offer_pipeline) skjer i UI-laget, ikke her: når `pipelineFields`
 * er false oppfører funksjonen seg identisk med den gamle (pre-VIB-1304)
 * logikken (kun sent_at/accepted_at), slik at "mørkt bak flagg" gjelder helt
 * ned til hvilke kolonner som skrives.
 */

import type { OfferProbability } from "./offer-pipeline-calc";

export type OfferStatusPatchStatus = "draft" | "sent" | "accepted" | "lost";

const VALID_UNTIL_DAYS = 45;

export interface OfferStatusPatchCurrent {
  status: OfferStatusPatchStatus;
  sent_at: string | null;
  accepted_at: string | null;
  valid_until?: string | null;
  /** VIB-2094: sannsynlighetskategori. Påkrevd ved sending. */
  probability?: OfferProbability | null;
  /** VIB-2094: rådgivers estimat på når kunden bestemmer seg. Påkrevd ved sending. */
  expected_close_date?: string | null;
}

/** Hva som mangler før et tilbud kan sendes (VIB-2094). */
export interface MissingBeforeSend {
  probability: boolean;
  expectedCloseDate: boolean;
}

/**
 * VIB-2094 (Erik 28.09): rådgiver MÅ sette sannsynlighet og estimert
 * close-dato før tilbudet sendes.
 *
 * Returnerer null når ingenting mangler. Kalleren bruker dette til å åpne
 * dialogen FØR statusendringen, på samme måte som LostReasonDialog fanger opp
 * `lost` (VIB-1304) — `buildStatusPatch` kaster som sikkerhetsnett hvis noen
 * går utenom.
 *
 * Gjelder kun når pipeline-feltene er i bruk. Med flagget av finnes ikke
 * feltene i UI, og å blokkere sending på noe brukeren ikke kan fylle ut ville
 * gjort tilbud umulige å sende.
 */
export function missingBeforeSend(
  current: OfferStatusPatchCurrent,
  pipelineFields = true,
): MissingBeforeSend | null {
  if (!pipelineFields) return null;
  const missing: MissingBeforeSend = {
    probability: !current.probability,
    expectedCloseDate: !current.expected_close_date,
  };
  return missing.probability || missing.expectedCloseDate ? missing : null;
}

export interface OfferStatusPatchResult {
  status: OfferStatusPatchStatus;
  sent_at?: string;
  accepted_at?: string;
  valid_until?: string;
  lost_at?: string;
  lost_reason?: string;
  /** VIB-2094: satt ved sending når kalleren har samlet dem inn. */
  probability?: OfferProbability;
  expected_close_date?: string;
}

export interface BuildStatusPatchOptions {
  /** Injectable for tester. Default: new Date(). */
  now?: Date;
  /**
   * VIB-2094: sannsynlighet og close-dato kalleren samler inn i
   * SendOfferDialog. Slås sammen med patchen, og teller som oppfylt for
   * `missingBeforeSend`-kravet under.
   */
  sendFields?: { probability?: OfferProbability; expectedCloseDate?: string };
  /**
   * Påkrevd for status='lost' NÅR pipelineFields=true (Erik 15.07). Kastes
   * hvis mangler/tom — kalleren MÅ ha samlet begrunnelsen via
   * LostReasonDialog FØR denne kalles.
   */
  lostReason?: string;
  /**
   * Bak feature_offer_pipeline (default true for nye kallsteder). Når false:
   * gammel oppførsel (kun sent_at/accepted_at, ALDRI valid_until/lost_at/
   * lost_reason) — brukes av kallsteder som eksplisitt vil ha pre-VIB-1304-
   * semantikk mens flagget er av.
   */
  pipelineFields?: boolean;
}

function toIsoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/**
 * Bygger patch-objektet for en statusendring. Idempotent for
 * sent_at/accepted_at (settes kun hvis tom fra før, speiler eksisterende
 * handleStatusChange-oppførsel).
 */
export function buildStatusPatch(
  current: OfferStatusPatchCurrent,
  newStatus: OfferStatusPatchStatus,
  opts: BuildStatusPatchOptions = {},
): OfferStatusPatchResult {
  const now = opts.now ?? new Date();
  const pipelineFields = opts.pipelineFields ?? true;
  const patch: OfferStatusPatchResult = { status: newStatus };

  if (newStatus === "sent") {
    if (!current.sent_at) patch.sent_at = now.toISOString();
    if (pipelineFields && !current.valid_until) {
      const validUntil = new Date(now);
      validUntil.setUTCDate(validUntil.getUTCDate() + VALID_UNTIL_DAYS);
      patch.valid_until = toIsoDate(validUntil);
    }

    // VIB-2094: ta imot det kalleren samlet inn, og skriv det i samme patch
    // som statusen, så et tilbud aldri kan stå som sendt uten dem.
    const send = opts.sendFields;
    if (pipelineFields && send?.probability) patch.probability = send.probability;
    if (pipelineFields && send?.expectedCloseDate) {
      patch.expected_close_date = send.expectedCloseDate;
    }

    // Sikkerhetsnett, samme rolle som lost_reason-kastet under: kalleren SKAL
    // ha fanget dette med missingBeforeSend og åpnet SendOfferDialog først.
    const stillMissing = missingBeforeSend(
      {
        ...current,
        probability: patch.probability ?? current.probability,
        expected_close_date: patch.expected_close_date ?? current.expected_close_date,
      },
      pipelineFields,
    );
    if (stillMissing) {
      const fields = [
        stillMissing.probability ? "sannsynlighet" : null,
        stillMissing.expectedCloseDate ? "estimert close-dato" : null,
      ]
        .filter(Boolean)
        .join(" og ");
      throw new Error(`${fields} er påkrevd før tilbudet kan sendes (VIB-2094)`);
    }
  }

  if (newStatus === "accepted" && !current.accepted_at) {
    patch.accepted_at = now.toISOString();
  }

  if (newStatus === "lost" && pipelineFields) {
    const reason = opts.lostReason?.trim();
    if (!reason) {
      throw new Error(
        "lost_reason er påkrevd ved statusendring til 'lost' (Erik 15.07, VIB-1304)",
      );
    }
    patch.lost_at = now.toISOString();
    patch.lost_reason = reason;
  }

  return patch;
}
